import { useEffect, useState } from "react";
import { useTranslate } from "ra-core";
import { Pause, Play, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExperimentVideo } from "./ExperimentVideo";
import { formatClock, type AudibleSide } from "./syncedPlayback";
import { imageKey } from "./types";
import type {
  ExperimentArm,
  ExperimentManifest,
  ExperimentPhoto,
} from "./types";
import { useSyncedPlayback } from "./useSyncedPlayback";

const kindVariant = (kind?: ExperimentArm["kind"]) =>
  kind === "source" ? "outline" : kind === "control" ? "secondary" : "default";

/**
 * Before/after films of one item, side by side, in lockstep. Only one side is audible at a time:
 * flipping the sound mid-play compares the two soundtracks at the same frame.
 */
export const SyncedVideoCompare = ({
  manifest,
  arms,
  photo,
}: {
  manifest: ExperimentManifest;
  arms: ExperimentArm[];
  photo: ExperimentPhoto;
}) => {
  const translate = useTranslate();
  // Open on "before" (the source) vs the first candidate, skipping controls: that is the comparison
  // the entry exists for; the pickers reach every other pair.
  const [leftId, setLeftId] = useState(arms[0]?.id);
  const [rightId, setRightId] = useState(
    (
      arms.find((a) => a.kind === "candidate") ??
      arms[Math.min(1, arms.length - 1)]
    )?.id,
  );
  const left = arms.find((a) => a.id === leftId) ?? arms[0];
  const right = arms.find((a) => a.id === rightId) ?? arms[0];
  const player = useSyncedPlayback(`${photo.id}|${left.id}|${right.id}`);
  const { toggle, setAudible } = player;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLSelectElement
      )
        return;
      if (event.key === " ") {
        event.preventDefault();
        toggle();
      }
      if (event.key === "1") setAudible("left");
      if (event.key === "2") setAudible("right");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setAudible, toggle]);

  const selectClass = "border rounded-md bg-background px-2 py-1 text-sm";
  const sides: {
    side: AudibleSide;
    arm: ExperimentArm;
    ref: typeof player.left;
  }[] = [
    { side: "left", arm: left, ref: player.left },
    { side: "right", arm: right, ref: player.right },
  ];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        {(
          [
            ["crm.lab_results.wipe_left", left.id, setLeftId],
            ["crm.lab_results.wipe_right", right.id, setRightId],
          ] as const
        ).map(([label, value, onChange]) => (
          <label key={label} className="flex items-center gap-2">
            {translate(label)}
            <select
              className={selectClass}
              value={value}
              onChange={(e) => onChange(e.target.value)}
            >
              {arms.map((arm) => (
                <option key={arm.id} value={arm.id}>
                  {arm.label}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {sides.map(({ side, arm, ref }) => {
          const audible = player.audible === side;
          return (
            <figure key={side} className="space-y-1 min-w-0">
              <figcaption className="flex items-center justify-between gap-2 text-sm">
                <Badge variant={kindVariant(arm.kind)} className="truncate">
                  {arm.label}
                </Badge>
                <Button
                  variant={audible ? "default" : "outline"}
                  size="sm"
                  className="h-7 shrink-0"
                  onClick={() => player.setAudible(side)}
                  aria-pressed={audible}
                >
                  {audible ? (
                    <Volume2 className="h-4 w-4" />
                  ) : (
                    <VolumeX className="h-4 w-4" />
                  )}
                  {translate(
                    audible
                      ? "crm.lab_results.listening"
                      : "crm.lab_results.listen",
                  )}
                </Button>
              </figcaption>
              <div
                className={`bg-black rounded-md overflow-hidden flex items-center justify-center ring-2 ${
                  audible ? "ring-primary" : "ring-transparent"
                }`}
              >
                <ExperimentVideo
                  ref={ref}
                  media={manifest.videos[imageKey(photo.id, arm.id)]}
                  poster={manifest.images[imageKey(photo.id, arm.id)]}
                  label={`${photo.label ?? photo.id} – ${arm.label}`}
                  className="w-full max-h-[65vh] object-contain cursor-pointer"
                  onClick={player.toggle}
                />
              </div>
              {arm.description && (
                <p className="text-xs text-muted-foreground line-clamp-3">
                  {arm.description}
                </p>
              )}
            </figure>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" onClick={player.toggle} className="w-28">
          {player.playing ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4" />
          )}
          {translate(
            player.playing
              ? "crm.lab_results.pause_both"
              : "crm.lab_results.play_both",
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => player.seek(0)}
          title={translate("crm.lab_results.restart")}
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
        <input
          type="range"
          min={0}
          max={player.duration || 0}
          step={0.05}
          value={Math.min(player.time, player.duration || 0)}
          onChange={(e) => player.seek(Number(e.target.value))}
          className="flex-1 min-w-40"
          aria-label={translate("crm.lab_results.seek")}
        />
        <span className="text-xs tabular-nums text-muted-foreground">
          {formatClock(player.time)} / {formatClock(player.duration)}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        {translate("crm.lab_results.sync_help")}
      </p>
    </div>
  );
};
